import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

const PlanModal = ({ plan, onClose, onRefresh }) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    display_name: '',
    monthly_fee: '',
    first_month_fee: '',
    minutes_included: '',
    phone_numbers_included: '1',
    retention_days: '',
    voice_overage_rate: '',
    extra_phone_rate: '200',
    badge: '',
    is_popular: false,
    is_enterprise: false,
    is_public: true,
    sort_order: '0',
    is_active: true
  });

  const isEditing = !!plan;

  useEffect(() => {
    if (plan) {
      setFormData({
        id: plan.id || '',
        name: plan.name || '',
        display_name: plan.display_name || plan.name || '',
        monthly_fee: plan.monthly_fee ? (plan.monthly_fee / 100).toString() : '',
        first_month_fee: plan.first_month_fee ? (plan.first_month_fee / 100).toString() : '',
        minutes_included: plan.minutes_included?.toString() || '',
        phone_numbers_included: plan.phone_numbers_included?.toString() || '1',
        retention_days: plan.retention_days?.toString() || '',
        voice_overage_rate: plan.voice_overage_rate ? (plan.voice_overage_rate / 100).toString() : '',
        extra_phone_rate: plan.extra_phone_rate ? (plan.extra_phone_rate / 100).toString() : '2.00',
        badge: plan.badge || '',
        is_popular: !!plan.is_popular,
        is_enterprise: !!plan.is_enterprise,
        is_public: plan.is_public !== false,
        sort_order: plan.sort_order?.toString() || '0',
        is_active: plan.is_active !== false
      });
    }
  }, [plan]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.id.trim()) errors.push('Plan ID is required');
    if (!formData.name.trim()) errors.push('Plan name is required');
    if (!formData.monthly_fee || parseFloat(formData.monthly_fee) <= 0) errors.push('Monthly fee must be greater than 0');
    if (!formData.minutes_included || parseInt(formData.minutes_included) <= 0) errors.push('Minutes included must be greater than 0');
    if (!formData.retention_days || parseInt(formData.retention_days) <= 0) errors.push('Retention days must be greater than 0');
    if (!formData.voice_overage_rate || parseFloat(formData.voice_overage_rate) <= 0) errors.push('Voice overage rate must be greater than 0');
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await getToken();
      
      // Convert dollar amounts to cents for API
      const apiData = {
        ...formData,
        monthly_fee: Math.round(parseFloat(formData.monthly_fee) * 100),
        first_month_fee: formData.first_month_fee ? Math.round(parseFloat(formData.first_month_fee) * 100) : null,
        minutes_included: parseInt(formData.minutes_included),
        phone_numbers_included: parseInt(formData.phone_numbers_included),
        retention_days: parseInt(formData.retention_days),
        voice_overage_rate: Math.round(parseFloat(formData.voice_overage_rate) * 100),
        extra_phone_rate: Math.round(parseFloat(formData.extra_phone_rate) * 100),
        sort_order: parseInt(formData.sort_order)
      };

      const url = isEditing 
        ? `https://parrotmate.com/api/admin/plans/${plan.version_id}`
        : 'https://parrotmate.com/api/admin/plans';
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        onRefresh();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save plan');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      setError('Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateToggle = async () => {
    if (!isEditing) return;

    setLoading(true);
    try {
      const token = await getToken();
      
      const response = await fetch(`https://parrotmate.com/api/admin/plans/${plan.version_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          monthly_fee: Math.round(parseFloat(formData.monthly_fee) * 100),
          first_month_fee: formData.first_month_fee ? Math.round(parseFloat(formData.first_month_fee) * 100) : null,
          minutes_included: parseInt(formData.minutes_included),
          phone_numbers_included: parseInt(formData.phone_numbers_included),
          retention_days: parseInt(formData.retention_days),
          voice_overage_rate: Math.round(parseFloat(formData.voice_overage_rate) * 100),
          extra_phone_rate: Math.round(parseFloat(formData.extra_phone_rate) * 100),
          sort_order: parseInt(formData.sort_order),
          is_active: !formData.is_active
        })
      });

      if (response.ok) {
        setFormData(prev => ({ ...prev, is_active: !prev.is_active }));
        onRefresh();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update plan status');
      }
    } catch (error) {
      console.error('Error updating plan status:', error);
      setError('Failed to update plan status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={"fixed inset-0 bg-black/75 dark:bg-gray-900/75 flex items-center justify-center z-50"}>
      <div className={"bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={"text-xl font-semibold text-gray-900 dark:text-white"}>
            {isEditing ? 'Edit Plan' : 'Create New Plan'}
          </h3>
          <button
            onClick={onClose}
            className={"text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors dark:text-white"}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className={"mb-4 p-3 border border-red-600 rounded-lg bg-red-50 dark:bg-red-900/20"}>
            <p className={"text-sm text-red-700 dark:text-red-400"}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600"}>
                Plan ID *
              </label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                disabled={isEditing}
                placeholder="e.g., starter, pro, enterprise"
                className={"w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                required
              />
              <p className={"text-xs text-gray-600 dark:text-gray-300 mt-1 dark:text-gray-600"}>
                Unique identifier for the plan (cannot be changed after creation)
              </p>
            </div>

            <div>
              <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600"}>
                Plan Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Starter Plan"
                className={"w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                required
              />
            </div>

            <div>
              <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600"}>
                Display Name
              </label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                placeholder="Optional display name"
                className={"w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Badge
              </label>
              <input
                type="text"
                name="badge"
                value={formData.badge}
                onChange={handleInputChange}
                placeholder="e.g., Best Value, New"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} pt-6`}>
            <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Pricing</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Monthly Fee ($) *
                </label>
                <input
                  type="number"
                  name="monthly_fee"
                  value={formData.monthly_fee}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="29.99"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  First Month Fee ($)
                </label>
                <input
                  type="number"
                  name="first_month_fee"
                  value={formData.first_month_fee}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="9.99 (optional promotional pricing)"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Voice Overage Rate ($/minute) *
                </label>
                <input
                  type="number"
                  name="voice_overage_rate"
                  value={formData.voice_overage_rate}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0.35"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Extra Phone Rate ($/month)
                </label>
                <input
                  type="number"
                  name="extra_phone_rate"
                  value={formData.extra_phone_rate}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="2.00"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Plan Limits */}
          <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} pt-6`}>
            <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Plan Limits</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Minutes Included *
                </label>
                <input
                  type="number"
                  name="minutes_included"
                  value={formData.minutes_included}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="100"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Phone Numbers Included
                </label>
                <input
                  type="number"
                  name="phone_numbers_included"
                  value={formData.phone_numbers_included}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Retention Days *
                </label>
                <input
                  type="number"
                  name="retention_days"
                  value={formData.retention_days}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="30"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  required
                />
              </div>
            </div>
          </div>

          {/* Plan Settings */}
          <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} pt-6`}>
            <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Plan Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Sort Order
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mt-1`}>Lower numbers appear first</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_popular"
                    checked={formData.is_popular}
                    onChange={handleInputChange}
                    className={`rounded text-teal-600 focus:ring-teal-500 ${
                      theme === 'dark' 
                        ? 'border-gray-600 focus:ring-offset-gray-800' 
                        : 'border-gray-300 focus:ring-offset-white'
                    }`}
                  />
                  <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Mark as Popular</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_enterprise"
                    checked={formData.is_enterprise}
                    onChange={handleInputChange}
                    className={`rounded text-teal-600 focus:ring-teal-500 ${
                      theme === 'dark' 
                        ? 'border-gray-600 focus:ring-offset-gray-800' 
                        : 'border-gray-300 focus:ring-offset-white'
                    }`}
                  />
                  <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Enterprise Plan</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    className={`rounded text-teal-600 focus:ring-teal-500 ${
                      theme === 'dark' 
                        ? 'border-gray-600 focus:ring-offset-gray-800' 
                        : 'border-gray-300 focus:ring-offset-white'
                    }`}
                  />
                  <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Public Plan</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className={`rounded text-teal-600 focus:ring-teal-500 ${
                      theme === 'dark' 
                        ? 'border-gray-600 focus:ring-offset-gray-800' 
                        : 'border-gray-300 focus:ring-offset-white'
                    }`}
                  />
                  <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Active Plan</span>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex justify-between items-center pt-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
            <div>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleActivateToggle}
                  disabled={loading}
                  className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors disabled:opacity-50 ${
                    formData.is_active
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {formData.is_active ? 'Deactivate' : 'Activate'}
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  theme === 'dark'
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center transition-colors dark:text-white"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {isEditing ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanModal;
